import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useTheme } from "hooks/useTheme";
import { useTranslation } from "hooks/useTranslation";
import { useStrikeUpChat } from "hooks/useStrikeUpChat";
import { MdAutoAwesome } from "react-icons/md";
import { isCustomTriggerDisplay } from "services/widgetService";

/**
 * "Ask StrikeUp" page-header button. Renders only when the embedded widget
 * is configured in one of the custom-trigger modes — invisible otherwise so we
 * don't confuse customers running other widget configurations.
 *
 * Clicking opens the widget pane and forwards `context` as the seed message
 * (becomes a placeholder/assistant trigger inside the chat). Each page is
 * expected to pass a short string describing what the page is about so the
 * AI has immediate context.
 *
 * Visual signature: neutral white background, animated gradient border
 * (blue → purple → pink → blue) — distinguishes it from intent="primary"
 * buttons and signals AI capability.
 */

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Theme-aware: pass $isDark as a transient prop so it isn't forwarded to the DOM.
// Light mode keeps the original white surface; dark mode shifts to a subtle dark
// surface with brighter text + slightly more vivid gradient stops so the AI
// shimmer stays readable against a dark page background.
const StyledButton = styled.button<{ $isDark: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  background: ${(p) => (p.$isDark ? "#1f1f23" : "#ffffff")};
  color: ${(p) => (p.$isDark ? "#f6f6fc" : "#2d2d33")};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(p) =>
      p.$isDark
        ? "0 4px 14px rgba(139, 92, 246, 0.35)"
        : "0 4px 12px rgba(99, 102, 241, 0.18)"};
  }
  &:active {
    transform: translateY(0);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    padding: 2px;
    border-radius: 10px;
    background: ${(p) =>
      p.$isDark
        ? "linear-gradient(90deg, #60a5fa, #a78bfa, #f472b6, #a78bfa, #60a5fa)"
        : "linear-gradient(90deg, #2563eb, #8b5cf6, #ec4899, #8b5cf6, #2563eb)"};
    background-size: 300% 100%;
    animation: ${gradientShift} 4s linear infinite;
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  & > .ask-strikeup-icon,
  & > .ask-strikeup-text {
    position: relative;
  }

  & > .ask-strikeup-icon {
    display: inline-flex;
    color: ${(p) => (p.$isDark ? "#a78bfa" : "#8b5cf6")};
  }
`;

interface AskStrikeUpButtonProps {
  /**
   * Short string describing the current page. Forwarded into the chat as
   * the assistant's opening question so the user sees a contextual prompt
   * the moment the panel opens.
   */
  context: string;
  /**
   * Optional open-time context — additional facts the AI should know about the
   * user's current state (entity IDs, page state, dashboard filters, etc.).
   * Hidden from the user; injected into the AI prompt for the opening turn only.
   */
  openContext?: string;
  /** Override label. Defaults to t('app.askStrikeUp.button'). */
  label?: string;
}

export const AskStrikeUpButton = ({
  context,
  openContext,
  label,
}: AskStrikeUpButtonProps) => {
  const { t } = useTranslation();
  const { displayType } = useStrikeUpChat();
  const { isDark } = useTheme();

  if (!isCustomTriggerDisplay(displayType ?? undefined)) return null;

  const text = label ?? t("app.askStrikeUp.button");

  const handleClick = () => {
    window.StrikeUpChat?.open({ message: context, openContext });
  };

  return (
    <StyledButton
      type="button"
      onClick={handleClick}
      aria-label={text}
      $isDark={isDark}
    >
      <span className="ask-strikeup-icon">
        <MdAutoAwesome size={16} />
      </span>
      <span className="ask-strikeup-text">{text}</span>
    </StyledButton>
  );
};
